import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl, formatApiError, shortAddr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tag, Gavel, Wallet, ExternalLink, Loader2, ShieldCheck, Hexagon, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import RelistModal from "@/components/RelistModal";

export default function AssetDetail() {
  const { id } = useParams();
  const { user, walletAddress, connectWallet } = useAuth();
  const [asset, setAsset] = useState(null);
  const [txs, setTxs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [bidAmount, setBidAmount] = useState("");
  const [relistOpen, setRelistOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/assets/${id}`);
    setAsset(data);
    const t = await api.get(`/assets/${id}/transactions`);
    setTxs(t.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  if (!asset) return <div className="py-20 grid place-items-center text-zinc-500"><Loader2 className="animate-spin" /></div>;

  const isImage = (asset.file_type || "").startsWith("image/");
  const isVideo = (asset.file_type || "").startsWith("video/");
  const isAudio = (asset.file_type || "").startsWith("audio/");
  const isOwner = user && user.id === asset.owner_id;
  const isCreator = user && user.id === asset.creator_id;
  const isTeacher = user?.role === "teacher";

  const buy = async () => {
    if (!user) return toast.error("Login to buy");
    if (!walletAddress) {
      try { await connectWallet(); } catch { return toast.error("Connect wallet"); }
    }
    setBusy(true);
    try {
      const sig = `sig_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const { data } = await api.post(`/assets/${id}/buy`, {
        wallet_address: walletAddress || "0x0",
        wallet_signature: sig,
      });
      toast.success(`Purchased! Tx ${data.tx_hash.slice(0, 12)}…`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const bid = async () => {
    if (!bidAmount) return;
    if (!walletAddress) {
      try { await connectWallet(); } catch { return toast.error("Connect wallet"); }
    }
    setBusy(true);
    try {
      const sig = `sig_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      await api.post(`/assets/${id}/bid`, {
        wallet_address: walletAddress || "0x0",
        wallet_signature: sig,
        amount_eth: Number(bidAmount),
      });
      toast.success("Bid placed");
      setBidAmount("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    try {
      await api.post(`/assets/${id}/approve`);
      toast.success("Approved & published");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reject = async () => {
    try {
      await api.post(`/assets/${id}/reject`);
      toast.success("Rejected");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid lg:grid-cols-5 gap-10">
      {/* Media */}
      <div className="lg:col-span-3">
        <div className="card-surface overflow-hidden">
          <div className="aspect-square bg-[#0A0A0A] grid place-items-center">
            {isImage && <img src={fileUrl(asset.file_path)} alt={asset.title} className="w-full h-full object-contain" />}
            {isVideo && <video src={fileUrl(asset.file_path)} controls className="w-full h-full" />}
            {isAudio && (
              <div className="p-6 w-full">
                <Hexagon size={80} className="mx-auto text-cyan8r/40" strokeWidth={1.1} />
                <audio src={fileUrl(asset.file_path)} controls className="w-full mt-6" />
              </div>
            )}
            {!isImage && !isVideo && !isAudio && (
              <div className="text-center p-10">
                <Hexagon size={80} className="mx-auto text-cyan8r/40" strokeWidth={1.1} />
                <div className="mt-4 font-mono text-xs text-zinc-500 uppercase">
                  {(asset.original_filename || "").split(".").pop()} file
                </div>
                <a
                  href={fileUrl(asset.file_path)}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2 text-cyan8r text-sm"
                >
                  Open file <ExternalLink size={14} />
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Tx history */}
        <div className="mt-8">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Transaction History</div>
          <div className="card-surface divide-y divide-white/5">
            {txs.length === 0 && <div className="p-4 text-sm text-zinc-500">No transactions yet</div>}
            {txs.map((t) => (
              <div key={t.id} className="p-4 flex items-center justify-between text-sm">
                <div>
                  <div className="capitalize text-white">{t.type}</div>
                  <div className="font-mono text-xs text-zinc-500 mt-1">{shortAddr(t.tx_hash)}</div>
                </div>
                <div className="text-right">
                  {t.amount_eth ? <div className="font-mono">{t.amount_eth} ETH</div> : <div className="text-zinc-500 text-xs">mint</div>}
                  <div className="text-xs text-zinc-500 mt-1">{new Date(t.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="lg:col-span-2 flex flex-col gap-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider">
          <span className="px-2 py-1 rounded-full border border-white/10 bg-white/5 text-zinc-300">{asset.contract_type}</span>
          {asset.contract_type === "ERC-1155" && (
            <span className="px-2 py-1 rounded-full border border-white/10 bg-white/5 text-zinc-300">×{asset.edition_count}</span>
          )}
          {asset.approval_status === "pending" && (
            <span className="px-2 py-1 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300">Pending Approval</span>
          )}
          {asset.approval_status === "rejected" && (
            <span className="px-2 py-1 rounded-full border border-red-400/30 bg-red-400/10 text-red-300">Rejected</span>
          )}
        </div>

        <h1 className="font-display text-4xl tracking-tight">{asset.title}</h1>

        <Link to={`/profile/${asset.creator_id}`} className="text-sm text-zinc-400 hover:text-white">
          by <span className="text-white">{asset.creator_name}</span>{" "}
          <span className="text-zinc-500">· {asset.school_name}</span>
        </Link>

        <p className="text-sm text-zinc-400 leading-relaxed">{asset.description || "No description"}</p>

        <div className="card-surface p-5">
          {asset.list_type === "fixed" && (
            <>
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Fixed Price</div>
              <div className="font-display text-4xl mt-2 flex items-center gap-2"><Tag size={20} className="text-cyan8r" />{asset.price_eth} ETH</div>
              {!isOwner && user && (
                <Button
                  data-testid="buy-btn"
                  onClick={buy}
                  disabled={busy}
                  className="btn-web3 w-full mt-4 h-11 rounded-md text-sm font-medium"
                >
                  {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <Wallet size={16} className="mr-2" />}
                  Buy with WalletConnect
                </Button>
              )}
              {isOwner && (
                <button
                  data-testid="relist-btn-fixed"
                  onClick={() => setRelistOpen(true)}
                  className="w-full mt-4 h-11 rounded-md text-sm font-medium border border-white/15 hover:border-white/30 transition flex items-center justify-center gap-2"
                >
                  <RefreshCcw size={14} /> Update listing
                </button>
              )}
            </>
          )}
          {asset.list_type === "auction" && (
            <>
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-1"><Gavel size={12}/> Live Auction</div>
              <div className="font-display text-4xl mt-2">{asset.highest_bid_eth ?? 0} ETH</div>
              <div className="text-xs text-zinc-500 mt-1">Ends {asset.auction_end ? new Date(asset.auction_end).toLocaleString() : "—"}</div>
              {!isOwner && user && (
                <div className="mt-4 flex gap-2">
                  <Input
                    data-testid="bid-amount-input"
                    type="number"
                    step="0.001"
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                    className="bg-[#0A0A0A] border-white/10"
                    placeholder="Bid amount (ETH)"
                  />
                  <button data-testid="place-bid-btn" onClick={bid} disabled={busy} className="btn-web3 px-4 rounded-md text-sm font-medium">Bid</button>
                </div>
              )}
            </>
          )}
          {asset.list_type === "none" && (
            <div className="text-sm text-zinc-400">Not listed for sale.</div>
          )}
          {isOwner && asset.list_type !== "fixed" && (
            <button
              data-testid="relist-btn"
              onClick={() => setRelistOpen(true)}
              className="w-full mt-4 h-11 rounded-md text-sm font-medium border border-cyan8r/40 text-cyan8r hover:bg-cyan8r/10 transition flex items-center justify-center gap-2"
            >
              <RefreshCcw size={14} /> {asset.list_type === "none" ? "List for sale" : "Update auction"}
            </button>
          )}
        </div>

        {/* Profit split */}
        <div className="card-surface p-5">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Profit Split</div>
          <div className="flex h-2 rounded overflow-hidden">
            <div className="bg-cyan8r" style={{ width: asset.creator_role === "teacher" ? "98%" : "97%" }} />
            {asset.creator_role !== "teacher" && <div className="bg-emerald-400" style={{ width: "1%" }} />}
            <div className="bg-amber-400" style={{ width: "1%" }} />
            <div className="bg-violet-400" style={{ width: "1%" }} />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
            <div className="flex justify-between"><span className="text-zinc-500">Creator</span><span className="font-mono">{asset.creator_role === "teacher" ? "98%" : "97%"}</span></div>
            {asset.creator_role !== "teacher" && <div className="flex justify-between"><span className="text-zinc-500">Teacher</span><span className="font-mono">1%</span></div>}
            <div className="flex justify-between"><span className="text-zinc-500">School</span><span className="font-mono">1%</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Cure8r</span><span className="font-mono">1%</span></div>
          </div>
        </div>

        {/* Metadata */}
        <div className="card-surface p-5 text-xs space-y-2">
          <div className="flex justify-between"><span className="text-zinc-500">Token ID</span><span className="font-mono text-zinc-300">{asset.token_id}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Tx Hash</span><span className="font-mono text-zinc-300">{shortAddr(asset.tx_hash)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Owner</span><span className="font-mono text-zinc-300">{shortAddr(asset.owner_wallet)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">School</span><span className="text-zinc-300">{asset.school_name || "—"}</span></div>
        </div>

        {/* Teacher approval action */}
        {isTeacher && asset.approval_status === "pending" && (
          <div className="card-surface p-4 flex gap-2">
            <button data-testid="approve-btn" onClick={approve} className="btn-web3 flex-1 py-2 rounded-md text-sm flex items-center justify-center gap-2">
              <ShieldCheck size={14}/> Approve
            </button>
            <button data-testid="reject-btn" onClick={reject} className="flex-1 py-2 rounded-md text-sm border border-red-400/30 text-red-300 hover:bg-red-400/10">
              Reject
            </button>
          </div>
        )}
      </div>
      <RelistModal open={relistOpen} onOpenChange={setRelistOpen} asset={asset} onRelisted={load} />
    </div>
  );
}
